import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Loader2, MoreHorizontal, ArrowDown } from "lucide-react";
import { AgentSystemEvent } from "../../../redux/reducers/Execution";
import ThinkingEventItem from "./ThinkingEventItem";
import MarkdownRenderer from "../MarkdownRenderer";
import {
  eventKey,
  groupEvents,
  mergeThinkingEvents,
  parsePersistedThinkingEvents,
} from "../../utils/thinkingTab";

interface ThinkingTabContentProps {
  systemEvents: AgentSystemEvent[];
  isThisProjectExecuting: boolean;
  thinking: {
    content: string | undefined;
    isLoading: boolean;
  };
}

/**
 * Renders the full Thinking tab content with smart auto-scroll.
 *
 * Auto-scroll behavior:
 * - Engaged by default (new events scroll into view)
 * - Disengages when user scrolls up
 * - Re-engages when user scrolls back to bottom
 * - "Scroll to bottom" button appears when disengaged
 */
const ThinkingTabContent: React.FC<ThinkingTabContentProps> = ({
  systemEvents,
  isThisProjectExecuting,
  thinking,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);

  // Unified timeline: merge persisted S3 history with live systemEvents
  // so a follow-up chat after refresh doesn't drop the prior turn's view.
  const persistedParse = useMemo(
    () => parsePersistedThinkingEvents(thinking.content),
    [thinking.content],
  );
  const mergedEvents = useMemo(
    () => mergeThinkingEvents(persistedParse.events, systemEvents),
    [persistedParse.events, systemEvents],
  );
  // Set of identity keys for events that came from the persisted S3
  // history. Persisted tool_starts are inherently complete (they're from
  // a finished turn), so we render them as completed regardless of
  // whether the live tool_complete-counting logic finds a match — the
  // backend doesn't always emit a final tool_complete before turn end.
  const persistedKeys = useMemo(
    () => new Set(persistedParse.events.map(eventKey)),
    [persistedParse.events],
  );

  // Detect scroll position: disengage if not at bottom, re-engage if at bottom
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setIsAutoScrollEnabled(isAtBottom);
  }, []);

  // Auto-scroll when the merged timeline grows (live or persisted source).
  useEffect(() => {
    if (isAutoScrollEnabled && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [mergedEvents.length, isAutoScrollEnabled]);

  // Scroll to bottom on initial load of persisted content
  useEffect(() => {
    if (scrollRef.current && thinking.content) {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [thinking.content]);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      setIsAutoScrollEnabled(true);
    }
  }, []);

  const showScrollButton =
    !isAutoScrollEnabled &&
    (mergedEvents.length > 0 || !!persistedParse.legacyMarkdown);

  // --- Determine what content to render ---
  const renderContent = () => {
    // Unified timeline of persisted + live events.
    if (mergedEvents.length > 0) {
      const grouped = groupEvents(mergedEvents);

      // Total completions per tool name (across both sources).
      const totalCompletions: Record<string, number> = {};

      mergedEvents.forEach((e) => {
        if (e.eventType === "tool_complete" && e.toolName) {
          totalCompletions[e.toolName] =
            (totalCompletions[e.toolName] || 0) + 1;
        }
      });

      // Track cumulative starts per tool to correctly attribute completions
      const startsSoFar: Record<string, number> = {};

      return (
        <div className="space-y-3">
          {grouped.map(({ event, repeatCount }, index) => {
            if (event.eventType === "tool_start") {
              const count = repeatCount || 1;
              const toolName = event.toolName || "";
              const previousStarts = startsSoFar[toolName] || 0;
              startsSoFar[toolName] = previousStarts + count;
              // Persisted tool_starts are inherently complete (from a
              // finished turn). For live events, count matching
              // tool_completes by tool name.
              const isPersisted = persistedKeys.has(eventKey(event));
              const isCompleted =
                isPersisted ||
                (totalCompletions[toolName] || 0) >= previousStarts + count;

              // If execution is done, all tools are implicitly completed
              // (even without explicit tool_complete events)
              const isActive = isThisProjectExecuting && !isCompleted;
              const showAsCompleted = isCompleted || !isThisProjectExecuting;

              return (
                <ThinkingEventItem
                  key={`${event.timestamp}-${index}`}
                  event={event}
                  isActive={isActive}
                  isCompleted={showAsCompleted}
                  repeatCount={repeatCount}
                />
              );
            }

            return (
              <ThinkingEventItem
                key={`${event.timestamp}-${index}`}
                event={event}
                isActive={false}
                isCompleted={false}
              />
            );
          })}
          {isThisProjectExecuting && (
            <div className="flex items-center gap-2 pt-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
              <span className="text-sm font-medium text-muted-foreground">
                Agent working...
              </span>
            </div>
          )}
        </div>
      );
    }

    // Legacy markdown fallback (pre-JSON-event-format assets).
    if (!isThisProjectExecuting && persistedParse.legacyMarkdown) {
      return <MarkdownRenderer content={persistedParse.legacyMarkdown} />;
    }

    // Loading state
    if (!isThisProjectExecuting && thinking.isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p className="text-sm text-muted-foreground">
              Loading activity history...
            </p>
          </div>
        </div>
      );
    }

    // Waiting state
    if (isThisProjectExecuting) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p className="text-sm text-muted-foreground">
              Waiting for agent activity...
            </p>
          </div>
        </div>
      );
    }

    // Empty state
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-2 text-center">
          <MoreHorizontal className="h-8 w-8 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            Agent activity will appear here during processing
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="relative h-full">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-full overflow-y-auto"
      >
        <div className="p-6">{renderContent()}</div>
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-4 right-4 p-2 rounded-full bg-background border border-border shadow-md hover:bg-muted transition-colors"
          aria-label="Scroll to bottom"
        >
          <ArrowDown className="h-4 w-4 text-muted-foreground" />
        </button>
      )}
    </div>
  );
};

export default ThinkingTabContent;
